import {useEffect, useState} from 'react';
import {getRemoteConfig, fetchAndActivate, getValue, setCustomSignals} from 'firebase/remote-config';
import {firebaseApp} from './firebase';

const remoteConfig = getRemoteConfig(firebaseApp);

// Keep the fetch interval short — this app is low-traffic/internal, and a
// feature flag is only useful as an instant kill-switch if it actually
// refreshes reasonably quickly. Firebase's own default is 12h.
remoteConfig.settings.minimumFetchIntervalMillis = 5 * 60 * 1000; // 5 minutes

remoteConfig.defaultConfig = {
  eventgrid_provisioning_enabled: false,
};


const PRODUCTION_HOSTNAME = 'app.smartchargecontroller.ch';
const environment = typeof window !== 'undefined' && window.location.hostname === PRODUCTION_HOSTNAME
  ? 'production'
  : 'staging';

// fetchAndActivate() is shared across all hook instances so we only hit the
// network once per fetch-interval window, regardless of how many components
// read a flag.
let activationPromise = null;
const ensureActivated = () => {
  if (!activationPromise) {
    activationPromise = (async () => {
      try {
        await setCustomSignals(remoteConfig, {environment});
      } catch (err) {
        console.error('Remote Config setCustomSignals failed:', err);
      }
      try {
        await fetchAndActivate(remoteConfig);
      } catch (err) {
        console.error('Remote Config fetch failed, falling back to defaults:', err);
      }
    })();
  }
  return activationPromise;
};

/**
 * Reads a boolean Remote Config parameter. Returns `defaultValue` until the
 * initial fetch/activate completes, then re-renders with the live value.
 * Toggling the parameter in the Firebase console takes effect on the next
 * fetch — no rebuild or redeploy needed.
 */
export const useRemoteConfigFlag = (key, defaultValue = false) => {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    let cancelled = false;
    ensureActivated().then(() => {
      if (!cancelled) {
        setValue(getValue(remoteConfig, key).asBoolean());
      }
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return value;
};
